import React, { useContext, useEffect, useMemo, useState } from 'react'
import styles from './StoreClients.module.css'
import { Link } from 'react-router-dom'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { BASE_URL_API } from '../../References/URLs'
import AuthContext from '../../store/auth-context'
import { ArrowUpDown, Cake, CalendarCheck, Check, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, Circle, Eye, Phone, Plus, Search, SlidersHorizontal, Trash2, User, UsersRound, X } from 'lucide-react'
import { toast } from 'react-toastify'
import { convertDateToDdMmYyyy } from '../../Helper/calculations'

// Mobile-only bottom sheet options — same keys/handler as the desktop
// sortable table headers, just presented as tappable cards instead.
const SORT_OPTIONS = [
    { key: 'clientName', label: 'Name', Icon: User },
    { key: 'clientGender', label: 'Gender', Icon: UsersRound },
    { key: 'mobile', label: 'Phone', Icon: Phone },
    { key: 'dob', label: 'Birthdate', Icon: Cake },
    { key: 'createdOn', label: 'Registered On', Icon: CalendarCheck },
]

const StoreClients = () => {
    const asCtx = useContext(ActiveStoreContext)
    const authCtx = useContext(AuthContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [allUsers, setAllUsers] = useState([
        {
            client_name: 'Loading...',
            countryCode: '+91',
            mobile: 'Loading...',
            email: 'Loading...',
            dob: 'Loading...',
            doa: 'Loading...'
        },
        {
            client_name: 'Loading...',
            countryCode: '+91',
            mobile: 'Loading...',
            email: 'Loading...',
            dob: 'Loading...',
            doa: 'Loading...'
        },
        {
            client_name: 'Loading...',
            countryCode: '+91',
            mobile: 'Loading...',
            email: 'Loading...',
            dob: 'Loading...',
            doa: 'Loading...'
        },
        {
            client_name: 'Loading...',
            countryCode: '+91',
            mobile: 'Loading...',
            email: 'Loading...',
            dob: 'Loading...',
            doa: 'Loading...'
        }
    ]);
    const [filteredData, setFilteredData] = useState([])
    const [searchKey, setSearchKey] = useState('')
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
    const [isSortSheetOpen, setIsSortSheetOpen] = useState(false) // mobile-only bottom sheet

    /* ── selection ── */
    const [selectedItems, setSelectedItems] = useState([])
    
    /* ── bulk actions ── */
    const [bulkGenderValue, setBulkGenderValue] = useState('')
    const [isApplyingBulk, setIsApplyingBulk] = useState(false)
    const [showBulkPanel, setShowBulkPanel] = useState(false)

    const getClientsStats = async () => {
        const response = await fetch(BASE_URL_API + "getClientsData",
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error!")
        } else {
            const respo = await response.json();
            if (respo.status === "success") {
                setAllUsers(respo.response)
                setFilteredData(respo.response)
            } else {
                if (respo.message === "tokenExpired") {
                    authCtx.logout();
                } else {
                    console.log(respo);
                    setAllUsers([])
                    setFilteredData([])
                }
            }
        }
    }

    const filterData = (data, searchKey) => {
        const keyword = searchKey.trim().toLowerCase()
        if (!keyword) {
            setFilteredData(Array.isArray(data) ? data : [])
            return
        }
        setFilteredData((Array.isArray(data) ? data : []).filter(item =>
            Object.values(item || {}).some(value =>
                value?.toString().toLowerCase().includes(keyword)
            )
        ))
    }

    useEffect(() => {
        filterData(allUsers, searchKey)
    }, [allUsers, searchKey])


    useEffect(() => {
        if (activeStore !== null) {
            getClientsStats()
        }
    }, [activeStore?.storeIdentifier])

    const getSortValue = (item, key) => {
        switch (key) {
            case 'clientName':
                return item?.clientName?.toString().toLowerCase() || ''
            case 'clientGender':
                return item?.clientGender?.toString().toLowerCase() || ''
            case 'mobile': {
                const country = (!item?.countryCode || item.countryCode.trim().toLowerCase() === 'null' || item.countryCode.trim().toLowerCase() === 'undefined')
                    ? '+91'
                    : item.countryCode
                return `${country}-${item?.mobile || ''}`
            }
            case 'dob': {
                const dateStr = item?.dob ? item.dob.substring(0, 10) : ''
                const date = new Date(dateStr)
                return Number.isNaN(date.getTime()) ? Number.NEGATIVE_INFINITY : date.getTime()
            }
            case 'createdOn': {
                const created = item?.createdOn || item?.addedOn || ''
                const date = new Date(created)
                return Number.isNaN(date.getTime()) ? Number.NEGATIVE_INFINITY : date.getTime()
            }
            default:
                return ''
        }
    }

    const sortedData = useMemo(() => {
        if (!Array.isArray(filteredData)) {
            return []
        }

        if (!sortConfig.key) {
            return filteredData
        }

        const sorted = [...filteredData].sort((a, b) => {
            const aValue = getSortValue(a, sortConfig.key)
            const bValue = getSortValue(b, sortConfig.key)

            if (aValue === bValue) {
                return 0
            }

            if (aValue === '' || aValue === Number.NEGATIVE_INFINITY) {
                return 1
            }

            if (bValue === '' || bValue === Number.NEGATIVE_INFINITY) {
                return -1
            }

            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1
            }

            return sortConfig.direction === 'asc' ? -1 : 1
        })

        return sorted
    }, [filteredData, sortConfig])

    /* ─────────── Selection Helpers ─────────── */
    const allSelected = sortedData.length > 0 && selectedItems.length === sortedData.length
    const someSelected = selectedItems.length > 0 && !allSelected

    const toggleSelect = (item) => {
        const exists = selectedItems.find(p => p.clientStoreRelationIdentifier === item.clientStoreRelationIdentifier)
        setSelectedItems(exists
            ? selectedItems.filter(p => p.clientStoreRelationIdentifier !== item.clientStoreRelationIdentifier)
            : [...selectedItems, item]
        )
    }

    const toggleSelectAll = () => {
        if (allSelected) setSelectedItems([])
        else setSelectedItems([...sortedData])
    }

    const clearSelection = () => {
        setSelectedItems([])
        setBulkGenderValue('')
        setShowBulkPanel(false)
    }

    /* ─────────── Bulk Gender Update ─────────── */
    const applyBulkGender = async () => {
        if (!bulkGenderValue.trim()) {
            toast.warning("Please select a gender.")
            return
        }
        setIsApplyingBulk(true)
        try {
            const response = await fetch(BASE_URL_API + "bulkUpdateClientGender", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    clients: selectedItems.map(i => i.clientStoreRelationIdentifier),
                    gender: bulkGenderValue
                })
            })
            const respo = await response.json()
            if (respo?.status === "success") {
                const ids = new Set(selectedItems.map(i => i.clientStoreRelationIdentifier))
                setAllUsers(prev => prev.map(p =>
                    ids.has(p.clientStoreRelationIdentifier) ? { ...p, clientGender: bulkGenderValue } : p
                ))
                toast.success(`Gender updated for ${selectedItems.length} client(s).`)
                clearSelection()
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                    return
                }
                toast.warning(respo?.message || "Bulk gender update failed.")
            }
        } catch {
            toast.warning("Could not apply bulk gender update.")
        } finally {
            setIsApplyingBulk(false)
        }
    }

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                }
            }
            return { key, direction: 'asc' }
        })
    }

    const renderSortIcon = (key) => {
        const iconClass = sortConfig.key === key ? `${styles.sortIcon} ${styles.sortIconActive}` : styles.sortIcon

        if (sortConfig.key !== key) {
            return <ArrowUpDown size={14} className={iconClass} />
        }

        return sortConfig.direction === 'asc'
            ? <ChevronUp size={14} className={iconClass} />
            : <ChevronDown size={14} className={iconClass} />
    }

    if (activeStore === null) {
        return (
            <div className={styles.mainWrapper}>
                <div className={styles.mainContainer}>
                    <div className={styles.mainContent}>
                        <div className={styles.emptyStoreCard}>
                            <UsersRound size={28} strokeWidth={1.8} />
                            <h3>Select a store to continue</h3>
                            <p>Choose an active location from the sidebar to view and manage client records.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>
                <div className={styles.mainContent}>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageHeaderLeft}>
                            <span className={styles.statusPill}>Client Directory</span>
                            <h1 className={styles.pageTitle}>
                                <UsersRound size={15} className={styles.pageTitleIcon} strokeWidth={1.8} />
                                Store Clients
                            </h1>
                            <p className={styles.pageSubtitle}>Search, sort and quickly access every client profile for this store.</p>
                        </div>
                        <div className={styles.pageHeaderStats}>
                            <span className={styles.statChip}>{allUsers?.length || 0} Total</span>
                            <span className={styles.statChipActive}>{filteredData?.length || 0} Visible</span>
                        </div>
                        {/* Mobile-only — desktop keeps Add New Client down in .mainHeader;
                            on mobile it moves up here where there's room next to the title. */}
                        <Link to={`/add-clients`} className={styles.pageHeaderMobileAdd} title="Add New Client">
                            <Plus size={20} strokeWidth={2.2} />
                        </Link>
                    </div>

                    <div className={styles.mainHeader}>
                        <div className={styles.toolbarLeft}>
                            <div className={styles.tableSearch}>
                                <Search size={16} className={styles.searchIcon} />
                                <input
                                    placeholder="Search by name, phone, gender, date..."
                                    value={searchKey}
                                    onChange={(e) => setSearchKey(e.target.value)}
                                />
                                {!!searchKey && (
                                    <button
                                        type="button"
                                        className={styles.clearBtn}
                                        onClick={() => setSearchKey('')}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <div className={styles.resultsCount}>
                                {filteredData?.length || 0} client(s) found
                            </div>
                        </div>
                        {/* Mobile-only — replaces the old select-based sort row;
                            opens the bottom sheet below instead. */}
                        <button
                            type="button"
                            className={styles.mobileSortTrigger}
                            onClick={() => setIsSortSheetOpen(true)}
                            aria-label="Sort clients"
                            title="Sort clients"
                        >
                            <SlidersHorizontal size={18} />
                        </button>
                        <div className={styles.addNew}>
                            <Link to={`/add-clients`} title="Add New Client">
                                <Plus size={15} strokeWidth={2.2} />
                                <span className={styles.addNewLabel}>Add New Client</span>
                            </Link>
                        </div>
                    </div>

                    <div className={styles.mainBody}>
                        <div className={styles.tableWrapper}>
                            <div className={styles.tableHeader}>
                                <button className={styles.checkboxCol} onClick={toggleSelectAll} title={allSelected ? "Deselect all" : "Select all"}>
                                    {allSelected
                                        ? <CheckCircle2 size={18} color="#6d28d9" strokeWidth={2.2} />
                                        : someSelected
                                            ? <Check size={18} color="#6d28d9" strokeWidth={2.8} style={{ borderRadius: 4, background: 'rgba(109, 40, 217, 0.1)', padding: 2 }} />
                                            : <Circle size={18} color="#94a3b8" strokeWidth={1.5} />
                                    }
                                </button>
                                <div className={styles.tCount}>#</div>
                                <div
                                    className={`${styles.tFullname} ${styles.sortableHeader}`}
                                    onClick={() => handleSort('clientName')}
                                >
                                    <span>Full Name</span>
                                    {renderSortIcon('clientName')}
                                </div>
                                <div
                                    className={`${styles.tPhone} ${styles.sortableHeader}`}
                                    onClick={() => handleSort('clientGender')}
                                >
                                    <span>Gender</span>
                                    {renderSortIcon('clientGender')}
                                </div>
                                <div
                                    className={`${styles.tPhone} ${styles.sortableHeader}`}
                                    onClick={() => handleSort('mobile')}
                                >
                                    <span>Phone</span>
                                    {renderSortIcon('mobile')}
                                </div>
                                <div
                                    className={`${styles.tBirthdate} ${styles.sortableHeader}`}
                                    onClick={() => handleSort('dob')}
                                >
                                    <span>Birthdate</span>
                                    {renderSortIcon('dob')}
                                </div>
                                <div
                                    className={`${styles.tAnniversary} ${styles.sortableHeader}`}
                                    onClick={() => handleSort('createdOn')}
                                >
                                    <span>Registered On</span>
                                    {renderSortIcon('createdOn')}
                                </div>
                                <div className={styles.tAction}>Action</div>
                            </div>
                            <div className={styles.tableData}>
                                {sortedData?.length > 0 ? (
                                    sortedData?.map((item, index) => {
                                        const birthdate = convertDateToDdMmYyyy(item?.dob?.substring(0, 10))
                                        const registeredDate = convertDateToDdMmYyyy((item?.createdOn || item?.addedOn || '').substring(0, 10))
                                        const isSelected = selectedItems.some(p => p.clientStoreRelationIdentifier === item.clientStoreRelationIdentifier)

                                        return (
                                            <div
                                                key={item?.clientStoreRelationIdentifier || index}
                                                className={`${styles.eachTableRow} ${isSelected ? styles.rowSelected : ''}`}
                                            >
                                                <button 
                                                    className={styles.checkboxCol}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        toggleSelect(item)
                                                    }}
                                                    title={isSelected ? "Deselect" : "Select"}
                                                >
                                                    {isSelected
                                                        ? <CheckCircle2 size={18} color="#6d28d9" strokeWidth={2.2} />
                                                        : <Circle size={18} color="#cbd5e1" strokeWidth={1.5} />
                                                    }
                                                </button>
                                                <div className={styles.tCount}>{index + 1}</div>
                                                <Link
                                                    to={`/user/${item?.clientStoreRelationIdentifier}`}
                                                    className={styles.rowContent}
                                                >
                                                <div className={styles.tFullname}>{item?.clientName || 'Unnamed Client'}</div>

                                                {/* Grouped so mobile can lay Gender + Phone on one
                                                    compact line; display:contents on desktop keeps
                                                    both as plain flex siblings, unchanged there. */}
                                                <div className={styles.metaRow}>
                                                    <div className={styles.tPhone} data-label="Gender">{item?.clientGender || 'NA'}</div>
                                                    <div className={styles.tPhone} data-label="Phone">
                                                        {(!item?.countryCode || item.countryCode.trim().toLowerCase() === 'null' || item.countryCode.trim().toLowerCase() === 'undefined')
                                                            ? '+91'
                                                            : item.countryCode
                                                        }
                                                        -{item?.mobile || 'NA'}
                                                    </div>
                                                </div>

                                                {/* Same grouping trick for the two dates — icons
                                                    replace the "Birthdate"/"Registered On" text
                                                    labels on mobile so one line covers both. */}
                                                <div className={styles.datesRow}>
                                                    <div className={styles.tBirthdate} data-label="Birthdate">
                                                        <Cake size={12} className={styles.dateIcon} />
                                                        {birthdate !== 'Invalid date. Please provide a valid date string.' ? birthdate : 'NA'}
                                                    </div>
                                                    <div className={styles.tAnniversary} data-label="Registered On">
                                                        <CalendarCheck size={12} className={styles.dateIcon} />
                                                        {registeredDate !== 'Invalid date. Please provide a valid date string.' ? registeredDate : 'NA'}
                                                    </div>
                                                    <ChevronRight size={16} className={styles.rowChevron} />
                                                </div>

                                                <div className={styles.tAction}>
                                                    <span
                                                        className={styles.viewBtn}
                                                        title="View Full Details"
                                                    >
                                                        <Eye size={16} strokeWidth={2.1} />
                                                        <span className={styles.viewBtnLabel}>View Profile</span>
                                                    </span>
                                                </div>
                                                </Link>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className={styles.emptyState}>
                                        <UsersRound size={34} strokeWidth={1.8} />
                                        <h4>No clients found</h4>
                                        <p>Try changing search keywords or add a new client for this store.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Bulk Toolbar ── */}
                    {selectedItems.length > 0 && (
                        <div className={styles.bulkToolbar}>
                            <div className={styles.bulkLeft}>
                                <span className={styles.bulkCount}>{selectedItems.length} selected</span>
                            </div>
                            <div className={styles.bulkActions}>
                                <button
                                    className={styles.bulkBtn}
                                    onClick={() => setShowBulkPanel(!showBulkPanel)}
                                >
                                    Update Gender
                                </button>
                                <button className={styles.bulkDismissBtn} onClick={clearSelection}>
                                    <Trash2 size={13} /> Deselect All
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Bulk Panel ── */}
                    {selectedItems.length > 0 && showBulkPanel && (
                        <div className={styles.bulkPanel}>
                            <div className={styles.bulkPanelInner}>
                                <div className={styles.bulkPanelLabel}>
                                    Set gender for <strong>{selectedItems.length}</strong> selected client(s)
                                </div>
                                <div className={styles.bulkPanelRow}>
                                    <select
                                        className={styles.bulkSelect}
                                        value={bulkGenderValue}
                                        onChange={(e) => setBulkGenderValue(e.target.value)}
                                    >
                                        <option value="">Select gender…</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <button
                                        className={styles.bulkApplyBtn}
                                        onClick={applyBulkGender}
                                        disabled={isApplyingBulk || !bulkGenderValue}
                                    >
                                        {isApplyingBulk ? '…' : 'Apply to All'}
                                    </button>
                                    <button className={styles.bulkCloseBtn} onClick={() => {
                                        setShowBulkPanel(false)
                                        setBulkGenderValue('')
                                    }}>
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Mobile-only sort bottom sheet ── */}
                    {isSortSheetOpen && (
                        <>
                            <button
                                type="button"
                                className={styles.sortSheetBackdrop}
                                onClick={() => setIsSortSheetOpen(false)}
                                aria-label="Close sort options"
                            />
                            <div className={styles.sortSheet} role="dialog" aria-label="Sort clients">
                                <div className={styles.sortSheetHandle} />
                                <div className={styles.sortSheetHeader}>
                                    <div>
                                        <div className={styles.sortSheetTitle}>Sort Clients</div>
                                        <div className={styles.sortSheetSubtitle}>{filteredData?.length || 0} clients</div>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.sortSheetClose}
                                        onClick={() => setIsSortSheetOpen(false)}
                                        aria-label="Close"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <div className={styles.sortOptionsList}>
                                    {SORT_OPTIONS.map(({ key, label, Icon }) => {
                                        const isActive = sortConfig.key === key || (!sortConfig.key && key === 'clientName')
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                className={isActive ? `${styles.sortOptionCard} ${styles.sortOptionCardActive}` : styles.sortOptionCard}
                                                onClick={() => handleSort(key)}
                                            >
                                                <Icon size={18} className={styles.sortOptionIcon} />
                                                <span className={styles.sortOptionLabel}>{label}</span>
                                                {isActive && (
                                                    sortConfig.direction === 'asc'
                                                        ? <ChevronUp size={16} className={styles.sortOptionDirIcon} />
                                                        : <ChevronDown size={16} className={styles.sortOptionDirIcon} />
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div >
    )
}

export default StoreClients